import React from 'react'
import { observer } from 'mobx-react-lite'

import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'

import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'

import { useAccountStore, useGlobalCopyStore } from '../../common'
import { Goal, TrackType } from '../../core'

import { AccountAction } from './actions/AccountAction'
import { AmountToSaveAction } from './actions/AmountToSaveAction'
import { ChangeAccountAction } from './actions/ChangeAccountAction'
import { GoalNameAction } from './actions/GoalNameAction'
import { GoalStatusCard } from './GoalStatusCard'
import { MonthlyPaymentAction } from './actions/MonthlyPaymentAction'
import { OriginalBalanceAction } from './actions/OriginalBalanceAction'
import { InterestRateAction } from './actions/InterestRateAction'
import { RetirementAgeAction } from './actions/RetirementAgeAction'
import { BirthdayAction } from './actions/BirthdayAction'
import { AddRetirementAccountAction } from './actions/AddRetirementAccountAction'

interface GoalDetailsProps {
  goal: Goal
}

export const GoalDetails = observer(({ goal }: GoalDetailsProps) => {
  const { goals: copy } = useGlobalCopyStore()
  const { visibleAccounts: accounts } = useAccountStore()

  const account = React.useMemo(
    () => accounts.find((a) => a.guid === goal.account_guid),
    [accounts, goal],
  )

  const goalType = goal.track_type

  let goalTitle = copy.details_save_title
  if (goal.track_type === TrackType.DEBT_TRACK) {
    goalTitle = copy.details_debt_title
  } else if (goal.track_type === TrackType.RETIREMENT_TRACK) {
    goalTitle = copy.details_retirement_title
  }

  return (
    <Stack>
      <Stack mx={24}>
        <Text color="text.secondary" sx={{ mb: 4, mt: 24 }} variant="caption">
          {goalTitle}
        </Text>
        <GoalStatusCard goal={goal} isInDrawer={true} />

        <Text color="text.secondary" sx={{ mb: 4, mt: 24 }} variant="caption">
          {copy.details_title}
        </Text>
      </Stack>

      <List sx={{ bgcolor: 'background.paper' }}>
        {goalType !== TrackType.RETIREMENT_TRACK ? (
          <GoalNameAction goal={goal} />
        ) : (
          <ListItem sx={{ px: 24 }}>
            <ListItemText
              primary={
                <Stack alignItems="center" direction="row" justifyContent="space-between">
                  <Text variant="body1">{copy.details_goal_name}</Text>
                  <Text bold={true} color={'text.primary'} sx={{ mr: 4 }} variant="body1">
                    {copy.retirement}
                  </Text>
                </Stack>
              }
              slotProps={{ primary: { variant: 'body1' } }}
            />
          </ListItem>
        )}

        <Divider sx={{ ml: 24 }} />

        {(goalType === TrackType.SAVINGS_TRACK || goalType === TrackType.RETIREMENT_TRACK) && (
          <>
            <AmountToSaveAction goal={goal} />
            <Divider sx={{ ml: goalType === TrackType.RETIREMENT_TRACK ? 24 : 0 }} />
          </>
        )}

        {goal.track_type === TrackType.DEBT_TRACK && (
          <>
            <MonthlyPaymentAction goal={goal} />
            <Divider sx={{ ml: 24 }} />

            <InterestRateAction goal={goal} />
            <Divider sx={{ ml: 24 }} />

            <OriginalBalanceAction goal={goal} />
            <Divider variant="fullWidth" />
          </>
        )}

        {goal.track_type === TrackType.RETIREMENT_TRACK && (
          <>
            <RetirementAgeAction goal={goal} />
            <Divider sx={{ ml: 24 }} />

            <BirthdayAction goal={goal} />
            <Divider variant="fullWidth" />
          </>
        )}
      </List>

      <Text color="text.secondary" sx={{ mb: 4, ml: 24, mt: 12 }} variant="caption">
        {goal.track_type === TrackType.RETIREMENT_TRACK
          ? copy.details_accounts
          : copy.details_account}
      </Text>

      {goal.track_type !== TrackType.RETIREMENT_TRACK && account && (
        <>
          <AccountAction account={account} />
          <Divider />
        </>
      )}

      {goal.track_type === TrackType.RETIREMENT_TRACK && (
        <>
          <AddRetirementAccountAction goal={goal} />
          <Divider />
        </>
      )}

      {goal.track_type === TrackType.SAVINGS_TRACK && !goal.is_complete && (
        <ChangeAccountAction goal={goal} />
      )}
    </Stack>
  )
})
